import type { ProgramSummary } from '@/state/program/program-summary';
import { MIN_READABLE } from '@/state/program/program-summary';
import { formatDays, formatLift, formatRate } from './outcome-tones';
import { MetricGrid, MetricTile, Meter } from '../ConsolePanel';

export type KpiTilesProps = { summary: ProgramSummary };

/**
 * The programme in six numbers. Headless, like Performance's tiles: each tile names
 * itself, and a panel title above them would be words nobody needed.
 */
export const KpiTiles = ({ summary }: KpiTilesProps) => {
  const { winRate, decided, counts, wonByDeployment, medianWinLift, winLiftCount, launched, inFlight, months } =
    summary;
  const { medianDurationDays, medianLeadDays, statisticalWinners, winnersShipped, shippedRate } = summary;

  const perMonth = months.length ? launched / months.length : 0;
  const thin = decided > 0 && decided < MIN_READABLE;

  // The split is stated rather than implied: a deployed inconclusive test enters both
  // sides of this rate, and a reader should see how much of it that is.
  const byDeployment = wonByDeployment > 0 ? ` (${wonByDeployment} by deployment)` : '';
  const winRateNote = `${counts.won} won${byDeployment} of ${decided} decided${thin ? ' — too few to read' : ''}`;

  return (
    <section className="bg-panel border-line rounded-panel border p-3.5" data-tour="program-kpis">
      <MetricGrid>
        <MetricTile
          label="Win rate"
          value={formatRate(winRate)}
          valueClassName={winRate === null ? 'text-dim' : 'text-ok'}
          description={winRateNote}
        >
          <Meter percent={winRate ?? 0} className="bg-ok" />
        </MetricTile>

        <MetricTile
          label="Median winning lift"
          value={formatLift(medianWinLift)}
          valueClassName={medianWinLift === null ? 'text-dim' : 'text-ok'}
          description={
            winLiftCount === counts.won
              ? 'Point estimate vs control, conversions'
              : `Over the ${winLiftCount} of ${counts.won} wins with a measured lift`
          }
        />

        <MetricTile
          label="Launched"
          value={String(launched)}
          description={`${perMonth.toFixed(1)} per month · ${inFlight} running now`}
        />

        <MetricTile
          label="Median test length"
          value={formatDays(medianDurationDays)}
          description="Start to conclusion"
        />

        <MetricTile label="Idea to launch" value={formatDays(medianLeadDays)} description="Median, creation to start" />

        <MetricTile
          label="Winners shipped"
          value={formatRate(shippedRate)}
          description={`${winnersShipped} of ${statisticalWinners} statistical winners deployed or implemented`}
        >
          <Meter percent={shippedRate ?? 0} className="bg-bar-muted" />
        </MetricTile>
      </MetricGrid>
    </section>
  );
};
